import { deviceApi } from "#/api/deviceApi";
import { notificationApi } from "#/api/notificationApi";
import { showSuccessNotification } from "#/components/notifications";
import { useClient } from "#/hook/useClient";
import { NOTIFICATION_QUERY_KEYS, useNotificationActions } from "#/hooks/useNotificationActions";
import type { HawbIssue } from "#/types/manifest";
import type { Notification } from "#/types/notification";
import { formatHawbIssue, formatHawbLabel } from "#/utils/manifestUtils";
import { getItemStatusColor } from "#/utils/ColorUtils";
import { sanitizeItemStatusLabel } from "#/utils/StringUtils";
import { formatCurrency } from "#/utils/formatCurrency";
import { Anchor, Badge, Button, Drawer, MultiSelect, Text, TextInput } from "@mantine/core";
import { notifications as mantineNotifications } from "@mantine/notifications";
import { IconChecks, IconSearch } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { formatDateTime } from "#/utils/dateTimeUtils";
import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";

interface NotificationContent {
  issueDescription?: string;
  actionTaken?: string;
  scanDateTime?: string;
  deviceName?: string;
  deviceId?: number;
  itemId?: number;
  rfidTag?: string;
  productName?: string;
  productId?: number;
  currentStatus?: string;
  invalidStatuses?: string[];
  categoryStatuses?: string[];
  deviceStatuses?: string[];
  /** Scan-issue alerts are collapsed per open issue; older rows lack these. */
  occurrences?: number;
  lastOccurrenceAt?: string;
  /** Exit issue raised by a Validate exit on a C72 (Shipment Manifests). */
  hawbExit?: {
    hawbId: number;
    exitId: number;
    awbNumber: string;
    hawbNumber: string;
    deviceName: string | null;
    validatedAt: string;
    expected: number;
    read: number;
    issues: HawbIssue[];
  };
  dwell?: {
    totalStuck: number;
    totalValue: number;
    sites: string[];
    /** `id` is absent on notifications created before category links existed. */
    categories: { id?: number | null; name: string; count: number; value: number }[];
  };
}

/** Parsed `content`, or null when it is missing or not valid JSON. */
const parseContent = (content: string | null): NotificationContent | null => {
  if (!content) return null;
  try {
    return JSON.parse(content) as NotificationContent;
  } catch {
    return null;
  }
};

interface NotificationDrawerProps {
  opened: boolean;
  onClose: () => void;
  preselectedNotificationId?: number;
}

const NotificationDrawer = ({ opened, onClose, preselectedNotificationId }: NotificationDrawerProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { client } = useClient();
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [search, setSearch] = useState("");
  const [selectedStatusesToAdd, setSelectedStatusesToAdd] = useState<string[]>([]);
  const [isUpdating, setIsUpdating] = useState(false);

  const themeColor = client?.color || "#3f81ef";
  const { markAsRead, markAllAsRead } = useNotificationActions();

  const { data: notificationsData } = useQuery({
    queryKey: [...NOTIFICATION_QUERY_KEYS.all, search],
    queryFn: async () => {
      const response = await notificationApi.getAll({
        page: 1,
        limit: 100,
        search: search || undefined,
      });
      return response.data;
    },
    enabled: opened,
  });

  const handleSelect = (notification: Notification) => {
    if (!notification.seen) markAsRead.mutate(notification.id);
    setSelectedNotification(notification);
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead.mutate(undefined, {
      onSuccess: () => {
        showSuccessNotification({ message: t("notifications.allMarkedRead") });
      },
    });
  };

  const notifications = useMemo(() => notificationsData?.data || [], [notificationsData?.data]);
  const hasUnread = notifications.some((notification) => !notification.seen);
  const selectedDetails = useMemo(
    () => (selectedNotification ? parseContent(selectedNotification.content) : null),
    [selectedNotification],
  );

  // The bell marks a clicked notification as read before opening the drawer,
  // so here it only needs selecting.
  useEffect(() => {
    if (preselectedNotificationId && notifications.length > 0) {
      const notification = notifications.find((n) => n.id === preselectedNotificationId);
      if (notification) {
        setSelectedNotification(notification);
      }
    }
  }, [preselectedNotificationId, notifications]);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      size="95%"
      position="bottom"
      title={
        <div className="flex items-center gap-4">
          <Text fw={600} size="lg">{t("notifications.title")}</Text>
          {hasUnread && (
            <Button
              size="xs"
              variant="outline"
              color="black"
              leftSection={<IconChecks size={14} />}
              loading={markAllAsRead.isPending}
              onClick={handleMarkAllAsRead}
            >
              {t("notifications.markAllRead")}
            </Button>
          )}
        </div>
      }
      styles={{
        body: { height: "calc(95vh - 80px)" },
      }}
    >
      <div className="flex h-full gap-4">
        {/* Left side - Notification list */}
        <div className="w-1/3 border-r border-r-slate-200 pr-4 flex flex-col bg-neutral-50 border border-neutral-200 p-4 rounded-lg">
          <TextInput
            placeholder={t("notifications.searchPlaceholder")}
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mb-4"
          />

          {notifications.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <Text c="dimmed" size="sm">
                {t("notifications.noNotificationsFound")}
              </Text>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto flex flex-col gap-2">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`px-5 py-4 flex flex-col cursor-pointer items-start gap-1  border  shadow shadow-black/10 transition-all duration-200 rounded-lg ${
                    selectedNotification?.id === notification.id ? "bg-slate-200 border-black" : "bg-white border-slate-200 hover:bg-slate-100 hover:border-slate-300"
                  }`}
                  onClick={() => handleSelect(notification)}
                >
                  <div className="flex items-center justify-between w-full">
                    <Text fw={600} size="sm" className="text-slate-500">
                      {notification.title}
                    </Text>
                  </div>
                  <Text size="xs" className="text-slate-600 line-clamp-2 leading-relaxed">
                    {notification.description}
                  </Text>
                  <div className="flex items-center gap-2 mt-4">
                    <Text size="xs">
                      {formatDateTime(notification.createdAt, client)}
                    </Text>
                    {!notification.seen && (
                      <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: themeColor }} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right side - Notification details */}
        <div className="flex-1 flex flex-col">
          {selectedNotification ? (
            <>
              <div className="py-4">
                <Text fw={600} size="lg" >
                  {selectedNotification.title}
                </Text>
                <Text size="sm" c="dimmed">
                  {formatDateTime(selectedNotification.createdAt, client)}
                </Text>
                {selectedDetails?.occurrences !== undefined &&
                  selectedDetails.occurrences > 1 &&
                  selectedDetails.lastOccurrenceAt && (
                    <Text size="xs" c="dimmed">
                      {t("notifications.occurrences", {
                        count: selectedDetails.occurrences,
                        date: formatDateTime(selectedDetails.lastOccurrenceAt, client),
                      })}
                    </Text>
                  )}
              </div>

              <div className="flex-1 overflow-y-auto">

                {selectedNotification.content && (() => {
                  const details = selectedDetails;
                  if (!details) {
                    return (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <Text size="sm" c="dimmed">{t("notifications.unableToParseDetails")}</Text>
                      </div>
                    );
                  }
                  {
                    if (details.hawbExit) {
                      const exit = details.hawbExit;
                      return (
                        <div className="space-y-4">
                          <div className="bg-red-50 p-4 rounded-lg border border-red-200 flex flex-col gap-2">
                            <Text size="sm" fw={600} className="text-red-900">
                              {t("notifications.hawbExit.title", {
                                hawb: formatHawbLabel({
                                  awb_number: exit.awbNumber,
                                  hawb_number: exit.hawbNumber,
                                }),
                              })}
                            </Text>
                            <ul className="flex flex-col gap-1 text-sm text-red-800">
                              {(exit.issues ?? []).map((issue) => (
                                <li key={issue.code}>• {formatHawbIssue(issue, t)}</li>
                              ))}
                            </ul>
                          </div>
                          <div className="grid grid-cols-2 gap-3 text-sm text-gray-700">
                            <span>
                              <span className="font-medium">{t("notifications.hawbExit.read")}:</span>{" "}
                              {exit.read} / {exit.expected}
                            </span>
                            <span>
                              <span className="font-medium">{t("notifications.hawbExit.device")}:</span>{" "}
                              {exit.deviceName || "--"}
                            </span>
                            <span className="col-span-2">
                              <span className="font-medium">{t("notifications.hawbExit.validatedAt")}:</span>{" "}
                              {formatDateTime(exit.validatedAt, client)}
                            </span>
                          </div>
                          <Button
                            color="black"
                            size="xs"
                            onClick={() => {
                              onClose();
                              void navigate(`/fleet/shipment-manifests/${exit.hawbId}`);
                            }}
                          >
                            {t("notifications.hawbExit.openHawb")}
                          </Button>
                        </div>
                      );
                    }
                    if (details.dwell) {
                      const d = details.dwell;
                      const tiles = [
                        { label: t("notifications.dwell.itemsStuck"), value: String(d.totalStuck) },
                        { label: t("notifications.dwell.sites"), value: String(d.sites.length) },
                        { label: t("notifications.dwell.value"), value: formatCurrency(d.totalValue) },
                      ];
                      return (
                        <div className="space-y-4">
                          <div className="grid grid-cols-3 gap-3">
                            {tiles.map((tile) => (
                              <div
                                key={tile.label}
                                className="flex flex-col gap-1 rounded-lg border border-red-200 bg-red-50 p-3"
                              >
                                <span className="text-xs font-semibold uppercase tracking-wide text-red-700">
                                  {tile.label}
                                </span>
                                <span className="text-xl font-extrabold text-red-600">{tile.value}</span>
                              </div>
                            ))}
                          </div>

                          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 flex flex-col gap-2">
                            <Text size="sm" fw={600} className="text-slate-500">
                              {t("notifications.dwell.byCategory")}
                            </Text>
                            {d.categories.map((cat) => (
                              <div key={cat.id ?? cat.name} className="flex items-center justify-between text-sm">
                                <div className="flex items-center gap-2">
                                  <span className="text-slate-700">{cat.name}</span>
                                  {typeof cat.id === "number" && (
                                    <Anchor
                                      size="xs"
                                      onClick={() => window.open(`/inventory/categories/${cat.id}`, "_blank")}
                                      className="cursor-pointer"
                                      underline="always"
                                    >
                                      {t("common.view")}
                                    </Anchor>
                                  )}
                                </div>
                                <span className="text-slate-600">
                                  {t("common.items", { count: cat.count })} · {formatCurrency(cat.value)}
                                </span>
                              </div>
                            ))}
                          </div>

                          {d.sites.length > 0 && (
                            <div className="text-sm text-gray-600">
                              <span className="font-medium">{t("notifications.dwell.locations")}:</span>{" "}
                              {d.sites.join(", ")}
                            </div>
                          )}
                        </div>
                      );
                    }
                    return (
                      <div className="space-y-4">

                        {details.issueDescription && (
                          <div className="bg-red-50 p-4 rounded-lg border border-red-200 flex flex-col gap-2">
                            <Text size="sm" fw={600} className="text-red-900">{t("notifications.issue")}</Text>
                            <Text size="sm" className="text-red-800">{details.issueDescription}</Text>
                          </div>
                        )}

                        {details.actionTaken && (
                          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 flex flex-col gap-2">
                            <Text size="sm" fw={600} className="text-blue-900">{t("notifications.actionTaken")}</Text>
                            <Text size="sm" className="text-blue-800">{details.actionTaken}</Text>
                          </div>
                        )}

                        <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 flex flex-col gap-4">
                          <Text size="sm" fw={600} className="text-slate-500">{t("notifications.scanDetails")}</Text>
                          <div className="space-y-2 text-sm grid grid-cols-2">
                            {details.scanDateTime && (
                              <div className="flex">
                                <span className="font-semibold w-32 text-slate-700">{t("notifications.dateTime")}</span>
                                <span className="text-slate-600">{details.scanDateTime}</span>
                              </div>
                            )}
                            {details.deviceName && (
                              <div className="flex">
                                <span className="font-semibold w-32 text-slate-700">{t("notifications.device")}</span>
                                <div className="flex items-center gap-2">
                                  <span className="text-slate-600">{details.deviceName} (ID: {details.deviceId})</span>
                                  <Anchor
                                    size="xs"
                                    onClick={() => window.open(`/devices/${details.deviceId}`, '_blank')}
                                    className="cursor-pointer"
                                    underline="always"
                                  >
                                    {t("common.view")}
                                  </Anchor>
                                </div>
                              </div>
                            )}
                            {details.itemId && (
                              <div className="flex">
                                <span className="font-semibold w-32 text-slate-700">{t("notifications.itemId")}</span>
                                <div className="flex items-center gap-2">
                                  <span className="text-slate-600">{details.itemId}</span>
                                  <Anchor
                                    size="xs"
                                    onClick={() => window.open(`/inventory/items/${details.itemId}`, '_blank')}
                                    className="cursor-pointer"
                                    underline="always"
                                  >
                                    {t("common.view")}
                                  </Anchor>
                                </div>
                              </div>
                            )}
                            {details.rfidTag && (
                              <div className="flex">
                                <span className="font-semibold w-32 text-slate-700">{t("notifications.rfidTag")}</span>
                                <span className="text-slate-600">{details.rfidTag}</span>
                              </div>
                            )}
                            {details.productName && (
                              <div className="flex">
                                <span className="font-semibold w-32 text-slate-700">{t("notifications.categoryLabel")}</span>
                                <div className="flex items-center gap-2">
                                  <span className="text-slate-600">{details.productName}</span>
                                  {details.productId && (
                                    <Anchor
                                      size="xs"
                                      onClick={() => window.open(`/inventory/categories/${details.productId}`, '_blank')}
                                      className="cursor-pointer"
                                      underline="always"
                                    >
                                      {t("common.view")}
                                    </Anchor>
                                  )}
                                </div>
                              </div>
                            )}
                            {details.currentStatus && (
                              <div className="flex items-center">
                                <span className="font-semibold w-32 text-slate-700">{t("notifications.currentStatus")}</span>
                                <Badge color={getItemStatusColor(details.currentStatus)} variant="light">
                                  {sanitizeItemStatusLabel(details.currentStatus)}
                                </Badge>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex gap-4 border-t border-t-slate-300 pt-4">
                          {details.invalidStatuses && details.invalidStatuses.length > 0 && details.deviceId && (() => {
                            const missingStatuses = details.categoryStatuses?.filter(
                              (status: string) => !details.deviceStatuses?.includes(status)
                            ) || [];

                            if (missingStatuses.length === 0) return null;

                            const allStatuses = [...new Set([...(details.categoryStatuses || []), ...(details.deviceStatuses || [])])];
                            const currentDeviceStatuses = details.deviceStatuses || [];

                            return (
                              <div className="flex-1 bg-green-50 p-4 rounded-lg border border-green-200 flex flex-col gap-3">
                                <div className="mb-auto">
                                  <Text size="sm" fw={600} className="text-green-900">{t("notifications.actionToTake")}</Text>
                                  <Text size="xs" className="text-green-700 mt-1">
                                    {t("notifications.updateDeviceConfig")}
                                  </Text>
                                </div>

                                <MultiSelect
                                  label={t("notifications.deviceStatuses")}
                                  placeholder={t("notifications.selectStatuses")}
                                  data={allStatuses.map((status) => ({
                                    label: sanitizeItemStatusLabel(status),
                                    value: status,
                                  }))}
                                  value={selectedStatusesToAdd.length > 0 ? selectedStatusesToAdd : currentDeviceStatuses}
                                  onChange={setSelectedStatusesToAdd}
                                  searchable
                                  size="sm"
                                  hidePickedOptions={false}
                                  styles={{
                                    pill: { maxWidth: "fit-content" },
                                    input: { minHeight: "auto", height: "auto" },
                                    inputField: { flexWrap: "wrap" }
                                  }}
                                />

                                <Button
                                  size="sm"
                                  loading={isUpdating}
                                  disabled={selectedStatusesToAdd.length === 0 || JSON.stringify([...selectedStatusesToAdd].sort()) === JSON.stringify([...currentDeviceStatuses].sort())}
                                  style={{ backgroundColor: themeColor }}
                                  onClick={async () => {
                                    setIsUpdating(true);
                                    try {
                                      await deviceApi.update(details.deviceId, { selected_statuses: selectedStatusesToAdd });
                                      mantineNotifications.show({
                                        title: t("common.success"),
                                        message: t("notifications.deviceConfigUpdated"),
                                        color: "green",
                                      });
                                      setSelectedStatusesToAdd([]);
                                    } catch {
                                      mantineNotifications.show({
                                        title: t("common.error"),
                                        message: t("notifications.deviceConfigFailed"),
                                        color: "red",
                                      });
                                    } finally {
                                      setIsUpdating(false);
                                    }
                                  }}
                                >
                                  {t("notifications.updateDeviceConfiguration")}
                                </Button>
                              </div>
                            );
                          })()}

                          {(details.categoryStatuses || details.deviceStatuses || details.invalidStatuses) && (
                            <div className="flex-1 bg-amber-50/50 p-4 rounded-lg border border-amber-200 flex flex-col gap-4">
                              <Text size="sm" fw={600} className="text-amber-500">{t("notifications.currentStatusConfig")}</Text>
                              <div className="space-y-2 text-sm">
                                {details.categoryStatuses && (
                                  <div className="flex flex-col gap-1">
                                    <span className="font-semibold text-slate-700">{t("notifications.categoryStatuses")}</span>
                                    <div className="flex flex-wrap gap-1">
                                      {details.categoryStatuses.map((status: string, idx: number) => (
                                        <Badge key={idx} color={getItemStatusColor(status)} variant="light" size="sm">
                                          {sanitizeItemStatusLabel(status)}
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {details.deviceStatuses && (
                                  <div className="flex flex-col gap-1">
                                    <span className="font-semibold text-slate-700">{t("notifications.deviceStatusesLabel")}</span>
                                    <div className="flex flex-wrap gap-1">
                                      {details.deviceStatuses.map((status: string, idx: number) => (
                                        <Badge key={idx} color={getItemStatusColor(status)} variant="light" size="sm">
                                          {sanitizeItemStatusLabel(status)}
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {details.invalidStatuses && details.invalidStatuses.length > 0 && (
                                  <div className="flex flex-col gap-1">
                                    <span className="font-semibold text-slate-700">{t("notifications.invalidStatuses")}</span>
                                    <div className="flex flex-wrap gap-1">
                                      {details.invalidStatuses.map((status: string, idx: number) => (
                                        <Badge key={idx} color="red" variant="light" size="sm">
                                          {sanitizeItemStatusLabel(status)}
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }
                })()}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <Text c="dimmed" size="sm">
                {t("notifications.selectToViewDetails")}
              </Text>
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default NotificationDrawer;
